/* eslint-disable no-console */
import {
    ICorporationResponse,
    ILocationResponse,
    InterestTypes,
} from '@kcftech/prometheus-client';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { Environment } from 'utils/EnvironmentHelper';

const withTryCatch = (fn: () => void) => {
    try {
        fn();
    } catch (e) {
        console.error(e);
    }
};

export type PendoConfig = Readonly<{
    visitor: {
        id: string;
        email_domain: string;
        role: string;
        accepted_eula: boolean;
        identity_provider_name?: string;
        profile_acceleration_units: string;
        profile_pressure_units: string;
        profile_temperature_units: string;
        profile_velocity_units: string;
        profile_date_format: string;
        profile_time_format?: string;
        profile_time_zones: string;
        persona?: string;
        custom_persona?: string;
        persona_interests?: string;
        viewport_width: number;
        viewport_height: number;
        screen_width: number;
        screen_height: number;
        /**
         * ratio of the resolution in physical pixels to the resolution in CSS
         * pixels for the current display device. This will let us detect
         * browser zoom.
         *
         * Note that it should not be assumed that values other than 1 mean that
         * the user has explicitly zoomed their browser, however. Higher
         * resolution machines often offer the ability to change the pixel ratio
         * system-wide. Most often to "2", but sometimes to fractional values as
         * well.
         */
        device_pixel_ratio: number;
        color_theme: string;
    };
    account: {
        id?: string;
        account_name?: string;
        corporation_id?: string;
        corporation_name?: string;
        access_level?: string;
        service_subscription?: string;
        location_id?: string;
        location_name?: string;
    };
}>;

export type TrackMetaData = Readonly<{
    [key: string]: string | number | boolean;
}>;

export type Pendo = Readonly<{
    initialize: (options?: PendoConfig) => void;
    identify: (options?: PendoConfig) => void;
    track: (eventName: string, meta?: TrackMetaData | null) => void;
}>;

declare const pendo: Pendo | undefined;

export type PendoContextValue = Readonly<{
    config?: PendoConfig;
    /**
     * Sends event to Pendo.
     *
     * @param eventName name of the event
     * @param meta additional details about the event
     * @example
     * // event
     * usePendoContext()?.track('Accepted EULA');
     *
     * // event with data
     * usePendoContext()?.track(
     *   'Machine Voice Play',
     *   { indicatorId: 'ac08a732-19bf-49d4-a5b6-195d2f1b9714' }
     * );
     */
    track: (eventName: string, eventMeta?: TrackMetaData) => void;
}>;

function getAccountId(corporation?: ICorporationResponse, location?: ILocationResponse) {
    if (!corporation) {
        return undefined;
    }
    const corporationId = `corporation:${corporation.Id}`;
    if (!location) {
        return corporationId;
    }
    const locationId = `location:${location.Id}`;
    return `${corporationId}::${locationId}`;
}

function getAccountName(
    corporation?: ICorporationResponse,
    location?: ILocationResponse,
) {
    if (!corporation) {
        return undefined;
    }
    if (location) {
        return `${corporation.Name} > ${location.Name}`;
    }
    return corporation.Name;
}

function getPersonaInterests(personaInterests: InterestTypes[]) {
    if (personaInterests.length === 0) {
        return undefined;
    }
    return personaInterests.join(', ');
}

const Context = createContext<PendoContextValue | undefined>(undefined);

export type PendoContextProps = Readonly<{ children: ReactNode }>;

export function PendoProvider({ children }: PendoContextProps) {
    const isInitializedRef = useRef(false);
    const { user } = useUser();
    const { colorMode } = useTheme();
    const { corporation, location } = useRouteEntities();

    const config = useMemo(() => {
        if (!Environment.pendo || !user) {
            return undefined;
        }

        const pendoConfig: PendoConfig = {
            visitor: {
                id: `${Environment.pendo.EnvironmentName}-${user.Id}`,
                email_domain: user.Email.substring(user.Email.indexOf('@') + 1),
                role: user.ApplicationRole,
                accepted_eula: user.AcceptedEula,
                identity_provider_name: user.IdentityProviderName,
                profile_acceleration_units: user.Profile.AccelerationUnits,
                profile_pressure_units: user.Profile.PressureUnits,
                profile_temperature_units: user.Profile.TemperatureUnits,
                profile_velocity_units: user.Profile.VelocityUnits,
                profile_date_format: user.Profile.DateFormat,
                profile_time_format: user.Profile.TimeFormat,
                profile_time_zones: user.Profile.TimeZones,
                persona: user.Profile.Persona,
                custom_persona: user.Profile.CustomPersona,
                persona_interests: getPersonaInterests(user.Profile.PersonaInterests),
                screen_height: window.screen.height,
                screen_width: window.screen.width,
                viewport_height: window.innerHeight,
                viewport_width: window.innerWidth,
                device_pixel_ratio: window.devicePixelRatio,
                color_theme: colorMode,
            },
            account: {
                id: getAccountId(corporation, location),
                account_name: getAccountName(corporation, location),
                corporation_id: corporation?.Id,
                corporation_name: corporation?.Name,
                access_level: corporation?.AccessLevel,
                service_subscription: location?.Settings.SubscriptionService,
                location_id: location?.Id,
                location_name: location?.Name,
            },
        };

        return pendoConfig;
    }, [colorMode, corporation, location, user]);

    const pendoConfigRef = useRef(config);
    pendoConfigRef.current = config;

    const track = useCallback<PendoContextValue['track']>((eventName, eventMeta = {}) => {
        if (!Environment.pendo || !isInitializedRef.current) {
            return;
        }
        withTryCatch(() =>
            pendo?.track(eventName, {
                ...eventMeta,
                account_name: pendoConfigRef.current?.account.account_name ?? '',
            }),
        );
    }, []);

    const value = useMemo<PendoContextValue>(
        () => ({
            config,
            track,
        }),
        [config, track],
    );

    useEffect(() => {
        if (!Environment.pendo) {
            return;
        }
        if (!pendo) {
            console.warn('Cannot find Pendo library');
            return;
        }
        if (!isInitializedRef.current) {
            if (config) {
                withTryCatch(() => {
                    pendo.initialize(config);
                    isInitializedRef.current = true;
                });
            }
        } else if (config) {
            withTryCatch(() => pendo.identify(config));
        }
    }, [config]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePendo() {
    const ctx = useContext(Context);
    if (!ctx) {
        throw new Error('Missing <PendoProvider>');
    }
    return ctx;
}
